import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import i18n from '../../i18n';
import { SaveResultState } from '../../redux/reducers/getSaveData/saveResultReducer';
import { selectSaveResult } from '../../redux/selectors/exportSelectorPaths';
import { isValidationResult, SpectralDetail, ValidationInput } from './FormErrorContext';

export interface FormWarning {
  primaryKey: string;
  message: string;
  messageFr: string;
  level: string;
  componentId?: string;
  detail?: SpectralDetail;
} // Had to define as an alias because unable to import FormError into FormWarningContext, emergency fix, TBD refactoring PIER2-1703
// Context Props
interface FormWarningContextProps {
    warnings: FormWarning[];  // Array of form validation warnings
    addWarning: (warning: FormWarning) => void;
    addWarnings: (warnings: FormWarning[]) => void;
    removeWarning: (primaryKey: string | null) => void;
    clearWarnings: () => void;
    updateWarnings: (warnings: FormWarning[]) => void;
    warningDisplayed: boolean;
    setWarningDisplayed: (displayedOnce: boolean) => void;
}

// Create the context with a default value of undefined
const FormWarningContext = createContext<FormWarningContextProps | undefined>(undefined);

// Custom hook
export const useFormWarningContext = (): FormWarningContextProps => {
    const context = useContext(FormWarningContext);
    if (!context) {
        throw new Error('useFormWarningContext must be used within a FormWarningProvider');
    }
    return context;
};

// Provider props
interface FormWarningProviderProps {
    children: ReactNode;
}

export const FormWarningProvider = (props: FormWarningProviderProps) => {
    // Initial state of the form warning context
    const [warnings, setWarnings] = useState<FormWarning[]>([]);
    const [warningDisplayed, setWarningDisplayed] = useState(false);

    // Add an warning to the list
    const addWarning = (warning: FormWarning) => {
        setWarnings((prevWarnings) => [...prevWarnings, warning]);
    };
    const addWarnings = useCallback((warnings: FormWarning[]) => {
        setWarnings((prevWarnings) => [...prevWarnings, ...warnings]);
      }, []);

    // Remove an warning based on the primaryKey
    const removeWarning = (primaryKey: string | null) => {
        setWarnings((prevWarnings) => prevWarnings.filter((warning) => warning.primaryKey !== primaryKey));
    };

    // Clear all warnings
    const clearWarnings = useCallback(() => {
        setWarnings([]);
    }, []);

    // Set warnings
    const updateWarnings = (newWarnings: FormWarning[]) => {
        setWarnings(newWarnings);
    };

    return (
        <FormWarningContext.Provider value={{ warnings, addWarning, addWarnings, removeWarning, clearWarnings, updateWarnings, warningDisplayed, setWarningDisplayed }}>
            {props.children}
        </FormWarningContext.Provider>
    );
};

const normalizeResponse = (apiResponse: ValidationInput): FormWarning[] => {
  if (!apiResponse) return [];

  if (Array.isArray(apiResponse)) {
    return apiResponse as FormWarning[];
  }

  if (isValidationResult(apiResponse)) {
    return apiResponse.warnings;
  }

  return [];
};

// Custom hook to parse Spectral Validation Response into FormWarningContext
export const useFormWarningValidationResponse = (apiResponse: ValidationInput) => {
  const saveResultState: SaveResultState = useSelector(selectSaveResult)
  const { addWarnings, updateWarnings } = useFormWarningContext();

  useEffect(() => {
    const normalized = normalizeResponse(apiResponse);

    if (normalized.length === 0) return;

    const newWarnings: FormWarning[] = [];

    normalized.forEach((error) => {
      if (error.level === "warning") {
        const {
          primaryKey,
          componentId,
          message,
          messageFr,
          level,
          detail
        } = error;
  
        if (primaryKey === null) {
          newWarnings.push({
            primaryKey,
            message,
            messageFr: messageFr,
            level
          });
        } else {
          const parts = primaryKey.split(".");
  
          // CASE 1: direct componentId
          if (componentId) {
            newWarnings.push({
              primaryKey: componentId + "." + parts[1],
              message,
              messageFr: messageFr,
              level
            });
  
            // CASE 2: detail.componentId (string OR array)
          } else if (detail?.componentId) {
            const componentIds = Array.isArray(detail.componentId)
              ? detail.componentId
              : [detail.componentId];
  
            componentIds.forEach((compId) => {
              let updatedKey = "";
  
              if (parts.length === 4) {
                updatedKey = `${parts[0]}.${parts[1]}.${parts[2]}.${compId}.${parts[3]}`;
              } else if (parts.length === 2) {
                updatedKey = `${compId}.${parts[1]}`;
              } else {
                updatedKey = `${parts[0]}.${parts[1]}.${compId}.${parts[2]}`;
              }
  
              newWarnings.push({
                primaryKey: updatedKey,
                message,
                messageFr: messageFr,
                level
              });
            });
  
            // CASE 3: fallback
          } else {
            newWarnings.push({
              primaryKey,
              message,
              messageFr: messageFr,
              level
            });
          }
        }
      }
    });

    // single state update (prevents infinite loop)
    if (saveResultState.statuses.length > 1) {
      addWarnings(newWarnings);
    } else {
      updateWarnings(newWarnings)
    }
    
  }, [addWarnings, apiResponse, saveResultState.statuses.length, updateWarnings]);
}


export type MatchingStrategy = (primaryKey: string, key: string) => boolean

export const defaultMatcher: MatchingStrategy = (primaryKey, key) => {
    const parts = primaryKey.split(".")
    return parts.length > 1 && parts[1].toLowerCase() === key.toLowerCase()
}

export const incomeAttributeMatcher: MatchingStrategy = (primaryKey, key) => {
    const match = primaryKey.match(/\[(\d+)\]\.value/)
    return match ? match[1] === key : false
}

export const incomeAttributePercentMatcher: MatchingStrategy = (primaryKey, key) => {
    const match = primaryKey.match(/\[(\d+)\]\.percent/)
    return match ? match[1] === key : false
}

export const incomeAttributeDescMatcher: MatchingStrategy = (primaryKey, key) => {
    const match = primaryKey.match(/\[(\d+)\]\.description/)
    return match ? match[1] === key : false
}

export const exactMatcher: MatchingStrategy = (primaryKey, key) => {
    return primaryKey.toLowerCase() === key.toLowerCase()
}

export const getFormWarningByName = (warnings: FormWarning[], key: string, matcher: MatchingStrategy = defaultMatcher) => {
    return warnings.find(warning => {
        if (warning.primaryKey === null || key === undefined) return false
        return matcher(warning.primaryKey, key)
    })
}

export const getFormWarningsByName = (warnings: FormWarning[], key: string, matcher: MatchingStrategy = defaultMatcher) => {
    return warnings.filter(warning => {
        if (warning.primaryKey === null || key === undefined) return false
        return matcher(warning.primaryKey, key)
    })
}

export const getFormWarningMessageByName = (warnings: FormWarning[], key: string, matcher?: MatchingStrategy) => {
    const warning = getFormWarningByName(warnings, key, matcher)
    return warning ? (i18n.language === 'fr' ? warning.messageFr : warning.message) : undefined
}

export const getFormWarningMessageByWarning = (warning: FormWarning) => {
    return (i18n.language === 'fr' ? warning.messageFr : warning.message)
}

export const hasWarning = (warnings: FormWarning[], key: string, matcher?: MatchingStrategy) => {
    return getFormWarningByName(warnings, key, matcher) !== undefined
}

export const isProfileSpectralEnabled = (profile: string) => {
    const enabledProfiles = ["BIP", "HTL", "MTL", "LTC", "LLE", "RHO", "GLF", "GEV", "AGS"]
    return enabledProfiles.includes(profile)
}